import clearCache from "./clearCache";
import { forceSync } from "../syncWorker";
import database from "../database";

const QUEUE_STORAGE_KEY = "sync_pending_queue";

interface ClearStorageResult {
  success: boolean;
  reason?: string;
  pendingCount?: number;
}

/**
 * Limpa o storage local para logout.
 *
 * IMPORTANTE: Verifica se há dados pendentes antes de limpar.
 * Se offline com dados pendentes, bloqueia o logout para evitar perda de dados.
 *
 * @param syncStatus - Status atual do sync (0=idle, 1=syncing, 2=done)
 * @returns Promise com resultado da operação
 */
export default async function clearStorage(
  syncStatus: number,
): Promise<ClearStorageResult> {
  // 1. Verificar dados pendentes (agora em IndexedDB)
  let pendingCount = 0;
  try {
    const pendingQueue = await database.adapter.getLocal(QUEUE_STORAGE_KEY);
    const pendingItems = pendingQueue ? JSON.parse(pendingQueue) : [];
    pendingCount = pendingItems.filter(
      (item: any) => item.status === "pending" || item.status === "in_progress",
    ).length;
  } catch (e) {
    console.warn("[clearStorage] Erro ao verificar dados pendentes:", e);
    pendingCount = -1; // Desconhecido
  }

  // 2. Se há dados pendentes, verificar conectividade
  if (pendingCount !== 0) {
    if (!navigator.onLine) {
      console.warn("[clearStorage] Bloqueado: offline com dados pendentes");
      return {
        success: false,
        reason: "OFFLINE_WITH_PENDING_DATA",
        pendingCount: pendingCount === -1 ? undefined : pendingCount,
      };
    }

    // 3. Tentar sincronizar antes de limpar
    if (syncStatus !== 2) {
      console.log("[clearStorage] Tentando sync antes do logout...");
      try {
        await forceSync(false);
        // Aguardar um pouco para o sync completar
        await new Promise((resolve) => setTimeout(resolve, 30000));

        // Verificar se ainda há pendentes
        const stillPending = await database.adapter.getLocal(QUEUE_STORAGE_KEY);
        const stillPendingItems = stillPending ? JSON.parse(stillPending) : [];
        const stillPendingCount = stillPendingItems.filter(
          (item: any) =>
            item.status === "pending" || item.status === "in_progress",
        ).length;

        if (stillPendingCount > 0) {
          console.warn(
            "[clearStorage] Sync incompleto:",
            stillPendingCount,
            "itens restantes",
          );
          return {
            success: false,
            reason: "SYNC_INCOMPLETE",
            pendingCount: stillPendingCount,
          };
        }
      } catch (e) {
        console.error("[clearStorage] Erro no sync:", e);
        return {
          success: false,
          reason: "SYNC_FAILED",
          pendingCount,
        };
      }
    }
  }

  // 4. Deletar IndexedDB
  const deleteDB = (name: string): Promise<void> => {
    return new Promise((resolve) => {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => {
        console.log(`[clearStorage] Database ${name} deleted successfully`);
        resolve();
      };
      request.onerror = () => {
        console.warn(`[clearStorage] Couldn't delete database ${name}`);
        resolve();
      };
      request.onblocked = () => {
        console.warn(`[clearStorage] Database ${name} deletion blocked`);
        resolve();
      };
    });
  };

  await Promise.all([deleteDB("loki.db"), deleteDB("WatermelonIDBChecker")]);

  // 5. Limpar localStorage (agora descomentado - seguro pois verificamos pendentes)
  try {
    localStorage.clear();
    console.log("[clearStorage] localStorage cleared");
  } catch (e) {
    console.warn("[clearStorage] Erro ao limpar localStorage:", e);
  }

  // 6. Limpar cache
  await clearCache();

  console.log("[clearStorage] Logout completo");
  return { success: true };
}
